import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { api } from '@/lib/api/client'

// ---------------------------------------------------------------------------
// Active allocations on a case
// ---------------------------------------------------------------------------

export interface ActiveAllocation {
  allocationId: string
  caseId: string
  slot: 'TELE' | 'FIELD'
  agentUserId: string
  agentEmpId: string
  agentFullName: string
  allocatedByUserId: string | null
  allocatedByEmpId: string | null
  allocatedByFullName: string | null
  allocatedAt: string
}

export function useActiveAllocations(caseId: string | null | undefined) {
  return useQuery({
    queryKey: ['allocations', 'case', caseId],
    enabled: !!caseId,
    queryFn: async () => {
      const res = await api.get<ActiveAllocation[]>(`/v1/allocations/case/${caseId}`)
      return res.data
    },
    refetchInterval: 10_000,
  })
}

// ---------------------------------------------------------------------------
// Agents (drop-down on the Allocations page)
// ---------------------------------------------------------------------------

export interface AgentRow {
  userId: string
  empId: string
  fullName: string
}

/**
 * Allocatable users for a slot. TELE needs TELECALLER, FIELD needs
 * COLLECTION_AGENT — the server enforces the same rule on write, so passing
 * the slot keeps the drop-down from offering someone the allocate call would
 * then reject.
 */
export function useAgents(slot?: 'TELE' | 'FIELD') {
  return useQuery({
    queryKey: ['allocations', 'agents', slot ?? 'ANY'],
    staleTime: 60_000,
    queryFn: async () => {
      const res = await api.get<AgentRow[]>('/v1/allocations/agents', {
        params: { limit: 200, ...(slot ? { slot } : {}) },
      })
      return res.data
    },
  })
}

// ---------------------------------------------------------------------------
// Bulk allocate
// ---------------------------------------------------------------------------

export interface BulkAllocateRequest {
  caseIds: string[]
  slot: 'TELE' | 'FIELD'
  agentUserId: string
}

export interface BulkAllocateResponse {
  grantedCount: number
  grantedAllocationIds: string[]
  failures: { caseId: string; code: string; message: string }[]
}

export function useBulkAllocate() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (body: BulkAllocateRequest) => {
      const res = await api.post<BulkAllocateResponse>('/v1/allocations/bulk', body)
      return res.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['cases', 'list'] })
      qc.invalidateQueries({ queryKey: ['allocations'] })
    },
  })
}

// ---------------------------------------------------------------------------
// Bulk transfer — the "Reassign" verb (BUG-002). Unlike /bulk, a case that
// already has an ACTIVE allocation on the slot is MOVED (old row closed as
// TRANSFERRED, fresh ACTIVE for the new agent); unallocated cases are simply
// allocated. Same request/response shape as /bulk.
// ---------------------------------------------------------------------------

export function useBulkTransfer() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (body: BulkAllocateRequest) => {
      const res = await api.post<BulkAllocateResponse>('/v1/allocations/transfer', body)
      return res.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['cases', 'list'] })
      qc.invalidateQueries({ queryKey: ['allocations'] })
    },
  })
}

// ---------------------------------------------------------------------------
// Revoke
// ---------------------------------------------------------------------------

export function useRevokeAllocation(caseId?: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (params: { allocationId: string; reason?: string }) => {
      const res = await api.post<{ allocationId: string; changed: boolean }>(
        `/v1/allocations/${params.allocationId}/revoke`,
        { reason: params.reason ?? 'MANUAL_REVOKE' },
      )
      return res.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['cases', 'list'] })
      qc.invalidateQueries({ queryKey: ['allocations'] })
      if (caseId) qc.invalidateQueries({ queryKey: ['allocations', 'case', caseId] })
    },
  })
}
